<template>
  <div class="hair-categories-page">
    <!-- 页面标题 -->
    <div class="page-title">
      <h2>Our Product</h2>
      <p>Check out our latest products and services.</p>
    </div>

    <!-- 产品类别网格 -->
    <div class="categories-grid">
      <!-- 类别项 -->
      <div
        v-for="(category, index) in categoriesData"
        :key="index"
        :class="['category-item', `position-${category.position}`]"
        @mouseenter="hoveredCategory = index"
        @mouseleave="hoveredCategory = -1"
        @touchstart="hoveredCategory = index"
        @touchend="hoveredCategory = -1"
        @click="openPhotoWall(index)"
        style="cursor: pointer"
      >
        <div class="category-image-container">
          <img
            :src="category.imageUrl"
            :alt="category.name"
            class="category-image"
            :class="{ hovered: hoveredCategory === index }"
          />
        </div>
        <div class="category-name">
          {{ category.name }}
        </div>
      </div>

      <!-- 连接线装饰 -->
      <div class="connection-lines">
        <div class="line line-1"></div>
        <div class="line line-2"></div>
        <div class="line line-3"></div>
        <div class="line line-4"></div>
        <div class="line line-5"></div>
      </div>
    </div>

    <!-- 完美版：C位居中+滚动动画+弧形照片墙 -->
    <div class="photo-wall-modal" v-if="showPhotoWall">
      <!-- 关闭按钮 -->
      <div class="close-btn" @click="closePhotoWall">✕</div>

      <!-- 核心照片容器 -->
      <div class="photo-arc-container" ref="arcContainer">
        <!-- 照片轨道（实现滚动动画） -->
        <div
          class="photo-track"
          ref="photoTrack"
          :style="{
            transform: `translateX(${trackOffset}px)`,
            transition: isAnimating
              ? 'transform 0.8s cubic-bezier(0.23, 1, 0.32, 1)'
              : 'none',
          }"
        >
          <!-- 弧形照片项 -->
          <div
            v-for="(photo, idx) in currentPhotos"
            :key="idx"
            class="arc-photo-item"
            :style="{
              width: `${photoSize.width}px`,
              height: `${photoSize.height}px`,
              margin: `0 ${photoGap}px`,
              transform: getPhotoTransform(idx),
              opacity: getPhotoOpacity(idx),
              zIndex: getPhotoZIndex(idx),
            }"
            @click="setActivePhoto(idx)"
          >
            <img :src="photo" :alt="`Photo ${idx + 1}`" class="photo-img" />
          </div>
        </div>
      </div>

      <!-- 左右切换按钮（精准可点击） -->
      <div
        class="nav-btn prev-btn"
        @click="prevPhoto"
        :class="{ disabled: activeIdx === 0 }"
      >
        <span>←</span>
      </div>
      <div
        class="nav-btn next-btn"
        @click="nextPhoto"
        :class="{ disabled: activeIdx === currentPhotos.length - 1 }"
      >
        <span>→</span>
      </div>

      <!-- 底部文字（C位下方） -->
      <div class="photo-caption">{{ currentCategory.name }}</div>
    </div>
  </div>
</template>

<script>
export default {
  name: "HairProduct",
  data() {
    return {
      categoriesData: [
        {
          name: "Tape in Hair Extensions",
          imageUrl: require("../image/product/1.jpg"),
          position: "one",
          folder: "tape-in",
        },
        {
          name: "Pre-Bonded Hair Extensions",
          imageUrl: require("../image/product/2.jpg"),
          position: "two",
          folder: "pre-bonded",
        },
        {
          name: "Clip In Hair Extensions",
          imageUrl: require("../image/product/3.jpg"),
          position: "five",
          folder: "clip-in",
        },
        {
          name: "Genius Weft",
          imageUrl: require("../image/product/4.jpg"),
          position: "four",
          folder: "one-piece",
        },
        {
          name: "Nano Ring Hair",
          imageUrl: require("../image/product/5.jpg"),
          position: "three",
          folder: "hair-pieces",
        },
        {
          name: "Hair Weft",
          imageUrl: require("../image/product/6.jpg"),
          position: "six",
          folder: "hair-weft",
        },
        {
          name: "Hair Bulk",
          imageUrl: require("../image/product/7.jpg"),
          position: "seven",
          folder: "accessories",
        },
      ],
      hoveredCategory: -1,
      // 照片墙核心状态
      showPhotoWall: false,
      currentCategory: {},
      currentPhotos: [],
      activeIdx: 0, // 当前C位照片索引
      trackOffset: 0, // 轨道偏移量（控制滚动）
      isAnimating: false, // 动画状态锁
      // 布局参数（精准控制）
      photoSize: {
        width: 260, // 基础照片宽度
        height: 400, // 基础照片高度
      },
      photoGap: 20, // 照片间距（解决无间隙问题）
      arcRadius: 1500, // 弧形半径
      arcAngle: 6, // 单张照片弧形角度
      visibleCount: 7, // 可见照片数量
    };
  },
  mounted() {
    // 窗口大小变化重新计算
    window.addEventListener("resize", this.calcLayout);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.calcLayout);
  },
  methods: {
    // 打开照片墙（初始化）
    openPhotoWall(index) {
      const category = this.categoriesData[index];
      this.currentCategory = category;
      this.activeIdx = 0;
      this.trackOffset = 0;

      // 加载图片
      this.loadCategoryPhotos(category.folder);
      this.showPhotoWall = true;

      // 计算布局（DOM渲染完成后）
      this.$nextTick(() => {
        this.calcLayout();
        this.updateTrackOffset();
      });
    },

    // 关闭照片墙
    closePhotoWall() {
      this.showPhotoWall = false;
      this.currentPhotos = [];
    },

    // 加载对应类别图片
    loadCategoryPhotos(folderName) {
      try {
        const imagesContext = require.context(
          "../image/product/photos",
          true,
          /\.(png|jpe?g|gif|svg)$/
        );
        this.currentPhotos = imagesContext
          .keys()
          .filter((path) => path.includes(`/${folderName}/`))
          .map((path) => imagesContext(path));
      } catch (error) {
        console.error(`加载${folderName}图片失败:`, error);
        this.currentPhotos = [];
      }
    },

    // 计算基础布局
    calcLayout() {
      if (!this.$refs.arcContainer || this.currentPhotos.length === 0) return;

      // 响应式调整照片尺寸
      const screenWidth = window.innerWidth;
      if (screenWidth < 768) {
        this.photoSize = { width: 180, height: 280 };
        this.photoGap = 10;
        this.arcAngle = 4;
      } else if (screenWidth < 1200) {
        this.photoSize = { width: 220, height: 340 };
        this.photoGap = 15;
        this.arcAngle = 5;
      } else {
        this.photoSize = { width: 260, height: 400 };
        this.photoGap = 20;
        this.arcAngle = 6;
      }
    },

    // 更新轨道偏移（确保激活照片在C位）
    updateTrackOffset() {
      this.isAnimating = true;
      const containerWidth = this.$refs.arcContainer.clientWidth;
      const itemWidth = this.photoSize.width + 2 * this.photoGap;
      // 计算C位偏移量
      this.trackOffset =
        containerWidth / 2 -
        this.activeIdx * itemWidth -
        this.photoSize.width / 2;
      // 动画结束后解锁
      setTimeout(() => {
        this.isAnimating = false;
      }, 800);
    },

    // 获取照片弧形变换
    getPhotoTransform(idx) {
      const diff = idx - this.activeIdx;
      // 计算弧形角度和缩放
      const angle = diff * this.arcAngle;
      const scale = Math.max(1 - Math.abs(diff) * 0.08, 0.7);
      // 3D弧形变换
      return `
        rotateY(${angle}deg) 
        scale(${scale}) 
        translateY(${Math.abs(diff) * 8}px)
      `;
    },

    // 获取照片透明度（C位100%，两侧渐变）
    getPhotoOpacity(idx) {
      const diff = Math.abs(idx - this.activeIdx);
      return Math.max(1 - diff * 0.12, 1);
    },

    // 获取照片层级（C位最高）
    getPhotoZIndex(idx) {
      return 100 - Math.abs(idx - this.activeIdx);
    },

    // 设置C位照片
    setActivePhoto(idx) {
      if (idx === this.activeIdx || this.isAnimating) return;
      this.activeIdx = idx;
      this.updateTrackOffset();
    },

    // 上一张（左切换）
    prevPhoto() {
      if (this.activeIdx <= 0 || this.isAnimating) return;
      this.activeIdx -= 1;
      this.updateTrackOffset();
    },

    // 下一张（右切换）
    nextPhoto() {
      if (this.activeIdx >= this.currentPhotos.length - 1 || this.isAnimating)
        return;
      this.activeIdx += 1;
      this.updateTrackOffset();
    },
  },
};
</script>

<style lang="scss" scoped>
// 基础变量
$color-primary: #c8a985;
$bg-black: #000;
$white: #fff;
$shadow: 0 0 30px rgba(0, 0, 0, 0.7);

// 页面基础样式
.hair-categories-page {
  width: 100%;
  background: #fff;
  padding: 0 15px;
  overflow: hidden;
}

.page-title {
  text-align: center;
  margin-bottom: 80px;

  h2 {
    font-size: 36px;
    margin-bottom: 15px;
  }

  p {
    font-size: 16px;
    color: #777;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
  }
}

.categories-grid {
  width: 100%;
  max-width: 1200px;
  height: 700px;
  margin: 0 auto;
  position: relative;
}

.category-item {
  width: 183px;
  height: 400px;
  position: absolute;
  transition: all 0.4s ease;
  z-index: 2;

  &.position-one {
    top: 43%;
    left: 0%;
    transform: translate(-50%, -50%);
  }
  &.position-two {
    top: 28%;
    left: 16.5%;
    height: 400px;
    transform: translate(-50%, -50%);
  }
  &.position-three {
    top: 48%;
    left: 33%;
    transform: translate(-50%, -50%);
  }
  &.position-four {
    top: 33%;
    left: 49.5%;
    transform: translate(-50%, -50%);
  }
  &.position-five {
    top: 51%;
    left: 66%;
    height: 400px;
    transform: translate(-50%, -50%);
  }
  &.position-six {
    top: 25%;
    left: 82.5%;
    transform: translate(-50%, -50%);
  }
  &.position-seven {
    top: 45%;
    left: 99%;
    transform: translate(-50%, -50%);
  }

  .category-image-container {
    width: 100%;
    height: 100%;
    border-radius: 4px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    position: relative;

    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(200, 169, 133, 0.15);
      transition: all 0.3s ease;
    }
  }

  .category-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease, filter 0.5s ease;

    &.hovered {
      transform: scale(1.25);
      filter: brightness(1.1);
    }
  }

  .category-name {
    position: absolute;
    bottom: -35px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 14px;
    color: #3a3226;
    font-weight: 500;
    white-space: nowrap;
  }
}

.connection-lines {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;

  .line {
    position: absolute;
    background: #d2b48c;
    transform-origin: 0 0;
    opacity: 0.6;
    height: 1px;
  }

  .line-1 {
    width: 45%;
    top: 0%;
    left: 0%;
    transform: rotate(15deg);
  }
  .line-2 {
    width: 45%;
    top: 34%;
    left: 9%;
    transform: rotate(-30deg);
  }
  .line-3 {
    width: 45%;
    top: 40%;
    left: 20%;
    transform: rotate(30deg);
  }
  .line-4 {
    width: 45%;
    top: 0%;
    left: 66%;
    transform: rotate(60deg);
  }
  .line-5 {
    width: 45%;
    top: 29%;
    left: 63%;
    transform: rotate(-15deg);
  }
}

/* ==========================
   完美版：C位居中+滚动动画
========================== */
.photo-wall-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: #00000073;
  z-index: 9999;
  overflow: hidden;
}

// 关闭按钮
.close-btn {
  position: absolute;
  top: 30px;
  right: 40px;
  font-size: 40px;
  color: $white;
  cursor: pointer;
  z-index: 1000;
  transition: color 0.3s;

  &:hover {
    color: $color-primary;
  }
}

// 核心弧形照片容器
.photo-arc-container {
  width: 100%;
  height: 100%;
  position: relative;
  perspective: 3000px; // 3D透视
  perspective-origin: center 50%;
  display: flex;
  align-items: center;
  overflow: hidden;

  .photo-track {
    position: absolute;
    left: 0;
    top: 0;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    height: 100%;
  }

  .arc-photo-item {
    flex-shrink: 0;
    position: relative;
    transition: all 0.8s cubic-bezier(0.23, 1, 0.32, 1);
    border-radius: 4px;
    overflow: hidden;
    box-shadow: $shadow;

    .photo-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
}

// 切换按钮（高亮可点击）
.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  color: $white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  cursor: pointer;
  z-index: 1000;
  transition: all 0.4s;

  &.prev-btn {
    left: 50px;
  }
  &.next-btn {
    right: 50px;
  }

  &:hover {
    background: rgba(200, 169, 133, 0.4);
    transform: translateY(-50%) scale(1.1);
    box-shadow: 0 0 20px rgba(200, 169, 133, 0.6);
  }

  &.disabled {
    opacity: 0.2;
    cursor: not-allowed;
    transform: translateY(-50%);
    background: rgba(255, 255, 255, 0.05);
    box-shadow: none;
  }
}

// 底部文字（C位正下方）
.photo-caption {
  position: absolute;
  bottom: 60px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 32px;
  color: $white;
  text-shadow: 0 2px 15px rgba(0, 0, 0, 0.9);
  letter-spacing: 3px;
  text-align: center;
  z-index: 1000;
}

/* 响应式适配 */
@media (max-width: 1200px) {
  .nav-btn {
    width: 70px;
    height: 70px;
    font-size: 32px;
  }
  .photo-caption {
    font-size: 28px;
  }
}

@media (max-width: 768px) {
  .nav-btn {
    width: 60px;
    height: 60px;
    font-size: 28px;
    &.prev-btn {
      left: 20px;
    }
    &.next-btn {
      right: 20px;
    }
  }
  .photo-caption {
    font-size: 24px;
    bottom: 40px;
  }
  .close-btn {
    font-size: 32px;
    top: 20px;
    right: 20px;
  }
}

@media (max-width: 480px) {
  .nav-btn {
    width: 50px;
    height: 50px;
    font-size: 24px;
  }
  .photo-caption {
    font-size: 20px;
  }
}

/* 移动端原有布局适配 */
@media (max-width: 992px) {
  .categories-grid {
    height: auto !important;
    padding: 20px 0;
  }

  .category-item {
    position: static !important;
    transform: none !important;
    display: block;
    margin: 60px auto;
    width: 260px;
    height: 380px !important;

    .category-name {
      bottom: -40px;
      font-size: 15px;
      white-space: normal;
      text-align: center;
      max-width: 100%;
    }
  }

  .connection-lines {
    display: none !important;
  }
  .page-title {
    margin-bottom: 40px;
  }
}
</style>